import { styled } from '@linaria/react';
import { ScrollArea } from '@mantine/core';
import type { Ref } from 'react';
import type { Phase } from '../../strategy/initiative-performance/flow-by-phase-tab/flow-by-phase-tab.type';
import type { WorkPeriod } from '../work-periods.type';
import { PhaseBox } from './phase-box';
import { useScrollAreaWidth } from './work-phase-distribution.hooks';

type PhaseMetadata = {
  id: string;
  label: string;
  description: string;
  color: string;
};

type WorkPhaseDistributionChartPhasesProps = {
  containerRef: Ref<HTMLDivElement | null>;
  containerWidth: number;
  allPhaseMetadata: PhaseMetadata[];
  phasePercentages: Record<string, number> | null;
  selectedDate: string | null;
  selectedWorkPeriod: WorkPeriod | null;
  selectedPhase: Phase | string | null;
  tasksOpen: boolean;
  onPhaseClick: (forDate: string, phase: Phase, selectedWorkPeriodId?: string) => void;
};

export const WorkPhaseDistributionChartPhases = ({
  containerRef,
  containerWidth,
  allPhaseMetadata,
  phasePercentages,
  selectedDate,
  selectedWorkPeriod,
  selectedPhase,
  tasksOpen,
  onPhaseClick,
}: WorkPhaseDistributionChartPhasesProps) => {
  const isPhaseSelected = (phase: Phase | string) => selectedPhase === phase && tasksOpen;

  const scrollAreaWidth = useScrollAreaWidth(containerWidth, tasksOpen);

  const phaseCards = (
    <PhaseBoxContainer>
      {allPhaseMetadata.map((phase) => {
        const phaseId = phase.id;
        const phaseLabel = phase.label;
        const phaseDescription = phase.description;
        const phaseColor = phase.color;

        return (
          <PhaseBox
            key={phaseId}
            name={phaseLabel}
            color={phaseColor}
            description={phaseDescription}
            value={phasePercentages?.[phaseId] ?? 0}
            isPercentage={true}
            onClick={() => selectedDate && onPhaseClick(selectedDate, phaseId as Phase, selectedWorkPeriod?.id)}
            isSelected={isPhaseSelected(phaseId)}
          />
        );
      })}
    </PhaseBoxContainer>
  );

  return (
    <PhaseBoxContainerWrapper ref={containerRef}>
      <ScrollArea type="auto" offsetScrollbars="x" scrollbarSize={8} w={scrollAreaWidth}>
        {phaseCards}
      </ScrollArea>
    </PhaseBoxContainerWrapper>
  );
};

const PhaseBoxContainerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
  position: relative;
  min-width: 0;
`;

export const PhaseBoxContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  padding: 8px 0;
  width: 100%;
`;
